import { useMemo, useState } from 'react';

import {
  ChakraBox,
  ChakraFlex,
  FAIconName,
  FireflyButton,
  FireflyIconButton,
} from '@equip.health/ui';

import { NoteEvents } from '~/analytics';
import { EditNoteAccordionFooterNetworkStatus } from '~/components/maud/patients/note/EditNoteAccordionFooterNetworkStatus';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import { DeleteNoteModal } from '~/components/common/DeleteNoteModal';
import { useEditNoteDrawerContext } from '~/lib/contexts/EditNoteDrawerContext';
import { useEditPatientNoteFormContext } from '~/lib/contexts/EditPatientNoteFormContext';
import { useDeleteNoteMutation } from '~/store/services/notes/notes';

const TOP_LEVEL_ID = 'add-note';
type Props = {
  onSaveSigned: () => void;
  uploadInProgressForSignError: boolean;
};

export const EditPatientNoteFooter = ({
  onSaveSigned,
  uploadInProgressForSignError,
}: Props) => {
  const { trackEvent } = useAnalytics();
  const { currentUser } = useGlobalContext();
  const [openDeleteDraftModal, setOpenDeleteDraftModal] =
    useState<boolean>(false);
  const { createNoteError } = useEditNoteDrawerContext();
  const {
    note,
    lastSavedTime,
    closeNoteDrawerWithoutSaving,
    isLoading: isContextApiLoading,
    isAutosaveNoteLoading,
    isUpdateNoteLoading,
    autosaveError,
    saveAndCloseNoteDrawer,
    clearAutosaveDebounce,
  } = useEditPatientNoteFormContext();
  const noteId = note.noteExternalId;

  const [
    deleteNote,
    { isLoading: isDeleteNoteLoading, error: deleteNoteError },
  ] = useDeleteNoteMutation();

  const isNoteAuthor = note.authoredByProviderExternalId === currentUser.userId;

  const networkErrorString = useMemo((): string | null => {
    if (createNoteError) {
      return `Unable to ${noteId ? 'fetch' : 'create'} note`;
    }
    if (uploadInProgressForSignError)
      return 'Your attachment is still being uploaded.';
    if (autosaveError) return 'Unable to autosave';
    if (deleteNoteError) {
      return 'Unable to delete note';
    }

    return null;
  }, [
    createNoteError,
    noteId,
    autosaveError,
    deleteNoteError,
    uploadInProgressForSignError,
  ]);

  return (
    <>
      <DeleteNoteModal
        isDeleteNoteLoading={isDeleteNoteLoading}
        isOpen={openDeleteDraftModal}
        onConfirm={() => {
          clearAutosaveDebounce();
          deleteNote({
            authoredByProviderExternalId: currentUser.userId,
            noteId,
          }).then(() => {
            setOpenDeleteDraftModal(false);
            closeNoteDrawerWithoutSaving();
          });
        }}
        setIsOpen={setOpenDeleteDraftModal}
      />
      <ChakraFlex
        alignItems="center"
        background="neutral.background.primary"
        justifyContent="space-between"
        width="100%"
        paddingY="12px"
        paddingX="18px"
      >
        <FireflyIconButton
          ariaLabel="Delete note"
          icon={FAIconName.trashRegular}
          id={`${TOP_LEVEL_ID}_delete-note-btn`}
          isDisabled={isContextApiLoading || !isNoteAuthor}
          isLoading={isDeleteNoteLoading}
          onClick={() => {
            trackEvent(NoteEvents.deleteDraftNoteIconClicked, {
              Source: 'Note Form',
            });
            if (isNoteAuthor) setOpenDeleteDraftModal(true);
          }}
          variant="tertiary"
        />
        <ChakraFlex alignItems="center" gap="36px">
          <ChakraBox
            color={networkErrorString ? 'red.500' : 'mono.70'}
            fontSize="12px"
            lineHeight="16.8px"
          >
            <EditNoteAccordionFooterNetworkStatus
              isGetNoteDataLoading={isContextApiLoading}
              isNoteAutoSaving={isAutosaveNoteLoading}
              isUpdateNoteLoading={isUpdateNoteLoading}
              lastSavedTime={lastSavedTime}
              networkErrorString={networkErrorString}
            />
          </ChakraBox>
          <ChakraBox>
            <FireflyButton
              id={`${TOP_LEVEL_ID}__keep-as-draft`}
              isDisabled={!noteId || isContextApiLoading}
              isLoading={isUpdateNoteLoading}
              loadingText="Saving"
              onClick={() => {
                trackEvent(NoteEvents.saveAsDraftNoteFormButtonClicked, {
                  Source: 'Note Form',
                });
                saveAndCloseNoteDrawer();
              }}
              variant="tertiary"
            >
              Save as draft
            </FireflyButton>
          </ChakraBox>
          <FireflyButton
            id={`${TOP_LEVEL_ID}__sign-and-finalize`}
            isDisabled={!noteId || !isNoteAuthor || isContextApiLoading}
            isLoading={isUpdateNoteLoading}
            leftIcon={FAIconName.checkRegular}
            onClick={() => {
              trackEvent(NoteEvents.signAndFinalizeNoteFormButtonClicked, {
                Source: 'Note Form',
              });
              onSaveSigned();
            }}
          >
            Sign & finalize
          </FireflyButton>
        </ChakraFlex>
      </ChakraFlex>
    </>
  );
};
